import TTLCache from '@isaacs/ttlcache';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHolterRrCellsBeats } from '@/entities/beat';
import { SelectionAreaWrapperHandle } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  KEY_CANCEL,
  KEY_RECOIL,
  STRIP_BEAT_THUMBNAIL_INFO,
} from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { hashStringCode } from '@/shared/lib';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError } from '@/shared/lib/toastNotification';
import { BULK_ACTION_ENUM, LOAD_BEAT_STATUS } from '@/shared/model';
import {
  BEAT_HR_TAB_ID,
  selectBulkAction,
  selectBulkActionByCell,
  selectBulkActionSwap,
  selectActiveTab,
  selectBulkSwapBeats,
  selectBeatChanges,
  selectIsLoadingStrip,
  selectPageIndex,
  selectResetSwapBeats,
  selectSelectedStrip,
  selectSelectedTemplate,
  selectSwapBeatChanges,
  selectBulkBeatsChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { getStripIndex, getStripPosition } from '@/entities/strip';
import { logError } from '@/shared/lib/apiHelpers';
import { formatBeatStripRr, getListBeatTime } from '@/widgets/beats/ui/helper';
import {
  handleMarkReviewByCellOnly,
  handleMarkReviewByTemplateAndCell,
  handleMarkReviewByTemplateOnly,
  handleUpdateBeatReview,
  handleUpdateBulkAction,
  hasTemplateBeat,
} from '@/widgets/beats/ui/reviewBeatsHelper';
import {
  handleChangeTypeMultipleBeats,
  handleSwapsMultipleBeats,
  mapFilterHeatmapIds,
} from './handler';
import { dataChartHeatMap } from './heatMapChart/handler';
import type { StripBeatsProps } from './stripBeats';

const stripBeatsCache = new TTLCache({ ttl: 3600000, max: 3 });

/**
 * Drives the RR-interval beat-strip grid: paging, cached fetching, keyboard navigation and bulk beat edits on the beats and heart-rate tab. Binds the tab-1 keyed report-store slices, fetches pages via fetchHolterRrCellsBeats behind a chunked cache warmed in the background, moves the selected strip on arrow keys, applies bulk review, beat-type and channel-swap edits when a cell or template selection is armed, and clears the cache whenever filter, sort or selection changes.
 *
 * @param props - Study and profile identifiers, timezone offset and the callback receiving the page's beat timestamps.
 * @returns An object exposing the loaded holterBeats page, loading and selection state, thumbnail display settings, the selection-area ref and the beat click handler.
 */
const useStripBeats = ({
  studyId,
  profileId,
  timezoneOffset,
  callbackListDate,
}: Pick<StripBeatsProps, 'studyId' | 'profileId' | 'timezoneOffset' | 'callbackListDate'>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Toggles the loading flag covering the RR-interval beat-strip grid. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Replaces the single beat strip currently focused in the grid. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const loadingBeatStatus = useReportStore((s) => s.loadingBeatStatus);
  const setLoadingBeatStatus = useReportStore((s) => s.actions.setLoadingBeatStatus);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setIsRenderViewerEcg = useReportStore((s) => s.actions.setIsRenderViewerEcg);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const sizeThumbnailConfig = useReportStore((s) => s.sizeBeatThumbnailConfig);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const bulkAction = useReportStore(selectBulkAction(keyRecoil));
  const bulkActionByCell = useReportStore(selectBulkActionByCell(keyRecoil));
  const setBulkActionByCellAction = useReportStore((s) => s.actions.setBulkActionByCell);
  /**
   * Arms or clears the bulk action scoped to the selected RR cells. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkActionByCell = (v: Parameters<typeof setBulkActionByCellAction>[1]) =>
    setBulkActionByCellAction(keyRecoil, v);
  /**
   * Reads the bulk action armed for the RR-interval beat strips. Applies the tab-1 keyed bulk-action selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The currently armed bulk action.
   */
  const getBulkAction = () => selectBulkAction(keyRecoil)(useReportStore.getState());
  const setBulkActionAction = useReportStore((s) => s.actions.setBulkAction);
  /**
   * Arms or clears the bulk action covering the RR-interval beat strips. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkAction = (v: Parameters<typeof setBulkActionAction>[1]) =>
    setBulkActionAction(keyRecoil, v);
  const listSelectedCells = useReportStore((s) => s.listSelectedCells);
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Replaces the multi-strip selection produced by the drag-selection overlay. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStrip);
  /**
   * Reads how many beat strips match the active filter, the count that drives paging and cache chunking. Pulls totalStrip off a report-store snapshot rather than subscribing to it.
   *
   * @returns The total beat-strip count.
   */
  const getTotalStrip = () => useReportStore.getState().totalStrip;
  /**
   * Reads the RR heat-map cells the user currently has selected. Pulls listSelectedCells off a report-store snapshot rather than subscribing to it.
   *
   * @returns The list of selected RR-interval cells.
   */
  const getListSelectedCells = () => useReportStore.getState().listSelectedCells;
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the RR-interval beat-strip grid to another page. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  /**
   * Reads the per-beat edits still pending on the RR-interval tab. Applies the tab-1 keyed beat-changes selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The list of pending beat changes.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the per-beat edits pending on the RR-interval tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);
  /**
   * Reads the beat or rhythm tab currently active in the RR-interval view. Applies the tab-1 keyed active-tab selector to a getState snapshot so the value is current at call time rather than at render time.
   *
   * @returns The active tab identifier for the beats and heart-rate view.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  /**
   * Reads the bulk beat-type changes accumulated on the RR-interval tab. Applies the tab-1 keyed bulkBeatsChanges selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The accumulated bulk beat changes.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setBulkBeatsChangesAction = useReportStore((s) => s.actions.setBulkBeatsChanges);
  /**
   * Writes the accumulated bulk beat-type changes for the RR-interval tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkBeatsChanges = (v: Parameters<typeof setBulkBeatsChangesAction>[1]) =>
    setBulkBeatsChangesAction(keyRecoil, v);
  /**
   * Reads the beats excluded again from the pending bulk beat-type change on the RR-interval tab. Applies the tab-1 keyed resetBulkBeatsChanges selector to a getState snapshot.
   *
   * @returns The list of beats reset out of the bulk change.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Writes the beats excluded again from the pending bulk beat-type change. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the beat option bundle mirrored into the RR-interval tab state. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  /**
   * Reads the morphology template currently selected on the RR-interval tab. Applies the tab-1 keyed selectedTemplate selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The selected morphology template, or an empty object when nothing is selected.
   */
  const getSelectedTemplate = () => selectSelectedTemplate(keyRecoil)(useReportStore.getState());
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  /**
   * Reads the per-beat reviewed-flag edits waiting to be saved. Pulls reviewedBeatsChanges off a report-store snapshot rather than subscribing to it.
   *
   * @returns The list of pending reviewed-beat changes.
   */
  const getReviewedBeatsChanges = () => useReportStore.getState().reviewedBeatsChanges;
  /**
   * Reads the beats individually un-marked out of a bulk reviewed action. Pulls resetBulkReviewedBeats off a report-store snapshot rather than subscribing to it.
   *
   * @returns The list of beats reset out of the bulk reviewed marking.
   */
  const getResetBulkReviewedBeats = () => useReportStore.getState().resetBulkReviewedBeats;
  /**
   * Reads the bulk reviewed-beat markers accumulated across selected cells and templates. Pulls bulkReviewedBeats off a report-store snapshot rather than subscribing to it.
   *
   * @returns The accumulated bulk reviewed-beat entries.
   */
  const getBulkReviewedBeats = () => useReportStore.getState().bulkReviewedBeats;
  /**
   * Reads the per-beat channel-swap edits pending on the RR-interval tab. Applies the tab-1 keyed swapBeatChanges selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The list of pending swap-beat changes.
   */
  const getSwapBeatChanges = () => selectSwapBeatChanges(keyRecoil)(useReportStore.getState());
  const setSwapBeatChangesAction = useReportStore((s) => s.actions.setSwapBeatChanges);
  /**
   * Writes the per-beat channel-swap edits pending on the RR-interval tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSwapBeatChanges = (v: Parameters<typeof setSwapBeatChangesAction>[1]) =>
    setSwapBeatChangesAction(keyRecoil, v);
  const bulkActionSwap = useReportStore(selectBulkActionSwap(keyRecoil));
  /**
   * Reads the bulk channel-swap action queued on the RR-interval tab. Applies the tab-1 keyed bulkActionSwap selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The queued bulk swap action entries.
   */
  const getBulkActionSwap = () => selectBulkActionSwap(keyRecoil)(useReportStore.getState());
  const setBulkSwapBeatsAction = useReportStore((s) => s.actions.setBulkSwapBeats);
  /**
   * Writes the accumulated bulk channel-swap entries for the RR-interval tab. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBulkSwapBeats = (v: Parameters<typeof setBulkSwapBeatsAction>[1]) =>
    setBulkSwapBeatsAction(keyRecoil, v);
  /**
   * Reads the bulk channel-swap entries recorded on the RR-interval tab. Applies the tab-1 keyed bulkSwapBeats selector to a getState snapshot instead of subscribing to the store.
   *
   * @returns The accumulated bulk swap-beat entries.
   */
  const getBulkSwapBeats = () => selectBulkSwapBeats(keyRecoil)(useReportStore.getState());
  /**
   * Reads the beats excluded again from the pending bulk channel swap on the RR-interval tab. Applies the tab-1 keyed resetSwapBeats selector to a getState snapshot.
   *
   * @returns The list of beats reset out of the bulk swap.
   */
  const getResetSwapBeats = () => selectResetSwapBeats(keyRecoil)(useReportStore.getState());
  const setResetSwapBeatsAction = useReportStore((s) => s.actions.setResetSwapBeats);
  /**
   * Writes the beats excluded again from the pending bulk channel swap. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setResetSwapBeats = (v: Parameters<typeof setResetSwapBeatsAction>[1]) =>
    setResetSwapBeatsAction(keyRecoil, v);

  /**
   * Reads the reviewed-state filter narrowing the visible beat strips. Pulls visibleReviewedFilter off a report-store snapshot so each fetch picks up the current choice.
   *
   * @returns The reviewed filter currently applied.
   */
  const getVisibleReviewedFilter = () => useReportStore.getState().visibleReviewedFilter;
  /**
   * Reads the sort order applied to the beat strip list. Pulls beatsSortType straight off a report-store snapshot rather than subscribing to it.
   *
   * @returns The beat sort type currently selected.
   */
  const getBeatsSortType = () => useReportStore.getState().beatsSortType;
  const visibleReviewedFilter = useReportStore((s) => s.visibleReviewedFilter);
  const beatsSortType = useReportStore((s) => s.beatsSortType);
  const selectedTemplate = useReportStore(selectSelectedTemplate(keyRecoil));

  const [holterBeats, setHolterBeats] = useState<any[]>([]);
  const [selected, setSelected] = useState(new Set<string>());

  const stripPosition = useRef({ x: 0, y: 0 });
  const prevPageIndex = useRef(pageIndex.index);
  const lastCurrentCellsHighestPage = useRef(0);
  const arrowKeyToPrevPage = useRef(false);
  const selectionAreaWrapperRef = useRef<SelectionAreaWrapperHandle>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const cache = useRef(new Map());
  let isActiveQuery: boolean;

  /**
   * Replaces the set of beat cells highlighted by the drag-selection overlay. Hands the new identifier set to the local selection state, which an effect then resolves into the multi-strip selection held in the report store.
   *
   * @param newSelected - Identifier set of the beat cells now highlighted.
   * @returns Nothing.
   */
  const setSelectedBeat = (newSelected: Set<string>) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedBeats: any[] = [];
    _.forEach(selectedArr, (item) => {
      const id = item.split('-')[1];
      /**
       * The beat on the loaded page matching an identifier parsed out of a drag-selection key. Found by scanning holterBeats for a numeric id match, so it is undefined whenever the selection points at a beat outside the current page.
       *
       * @returns The matching beat, or undefined when it is not on the loaded page.
       */
      const selectedBeat = (holterBeats || []).find((beat) => beat.id === Number(id));
      if (selectedBeat) {
        selectedBeats.push(selectedBeat);
      }
    });
    setSelectedMultipleStrip((prev: any) => {
      if (prev.length === 0 && selectedBeats.length === 0) {
        return prev;
      }
      return selectedBeats;
    });
  }, [selected, holterBeats]);

  /**
   * Warms the strip-beats cache with the beat chunks that have not been loaded yet for the current RR-cell filter. Returns immediately when no chunks are pending, issues one fetchHolterRrCellsBeats call per chunk with a skip and take window clamped to the remaining beat count, merges every result into the cached beatsData under its chunk key, and on failure marks the in-flight cache entry false and logs the error.
   *
   * @param inputFilter - Base beat filter the per-chunk requests are built from.
   * @param totalBeats - Total beat count used to clamp the last chunk's take.
   * @param chunkSize - Number of beats fetched per chunk.
   * @param cacheChunks - Chunk indexes still missing from the cache.
   * @param cacheKey - Key the merged chunk data is stored under.
   * @param _cacheKey - Key of the in-flight marker cleared when the fetch fails.
   * @param lastResult - Previously cached payload to merge onto when the store read comes back empty.
   * @returns A promise resolving once the fetched chunks have been merged into the strip-beats cache.
   */
  const fetchBackgroundData = async ({
    inputFilter,
    totalBeats,
    chunkSize,
    cacheChunks,
    cacheKey,
    _cacheKey,
    lastResult = {} as any,
  }: {
    inputFilter: any;
    totalBeats: any;
    chunkSize: any;
    cacheChunks: any;
    cacheKey: any;
    _cacheKey: any;
    lastResult?: any;
  }) => {
    if (_.isEmpty(cacheChunks)) return;
    try {
      const getCached = ((await stripBeatsCache.get(cacheKey)) as any) || lastResult || {};
      const beatsCacheData = {
        beatsData: getCached.beatsData || {},
        chunks: [...(getCached?.chunks || []), ...(cacheChunks || [])],
      };
      const promisses: any[] = [];
      _.forEach(cacheChunks, (chunk: any) => {
        const skip = chunk * chunkSize;
        const take = Math.min(chunkSize, totalBeats - skip);
        if (take <= 0) {
          return;
        }
        const filter = {
          ...inputFilter,
          skip,
        };
        promisses.push(fetchHolterRrCellsBeats(filter, take, KEY_CANCEL.API_HOLTER_AI, false));
      });
      const results = await Promise.all(promisses);

      _.forEach(results, (result: any, idx) => {
        _.assign(beatsCacheData.beatsData, {
          [`chunk-${cacheChunks[idx]}`]: result || [],
        });
      });
      stripBeatsCache.set(cacheKey, beatsCacheData);
    } catch (err) {
      cache.current.set(_cacheKey, false);
      // logError(msg, error) (handler.ts, Task 7) requires both args; this call site only passes one - pre-existing.
      (logError as any)(err);
    }
  };

  /**
   * Cuts one display page of beats out of the chunked strip-beats cache together with its parallel status, channel, heat-map, reviewed and template arrays. Yields empty arrays when the requested chunk is absent, first appends the following chunk when the page straddles a chunk boundary, then slices every array by the page window rebased onto the chunk start offset.
   *
   * @param beatsData - Cached payload keyed by chunk name.
   * @param startIndex - Absolute index of the first beat on the page.
   * @param endIndex - Absolute index just past the last beat on the page.
   * @param currentChunk - Index of the chunk holding the start of the page.
   * @param chunkSize - Number of beats stored per chunk.
   * @returns An object carrying the page slices of beats, hesBeatStatus, beatChannels, beatHeatMapIds, beatReviewed and beatTemplateIds.
   */
  const getCachedDataSlice = (
    beatsData: any,
    startIndex: number,
    endIndex: number,
    currentChunk: number,
    chunkSize: number,
  ) => {
    const chunkKey = `chunk-${currentChunk}`;
    const data: any = beatsData[chunkKey] || {};
    if (_.isEmpty(data)) {
      return {
        beats: [],
        hesBeatStatus: [],
        beatChannels: [],
        beatHeatMapIds: [],
        beatReviewed: [],
        beatTemplateIds: [],
      };
    }

    const sliceChunkIdx = currentChunk * chunkSize;
    const beats = [...data.beats];
    const hesBeatStatus = [...data.hesBeatStatus];
    const beatChannels = [...data.beatChannels];
    const beatHeatMapIds = [...data.beatHeatMapIds];
    const beatReviewed = [...data.beatReviewed];
    const beatTemplateIds = [...data.beatTemplateIds];

    if (Math.floor(endIndex / chunkSize) > Math.floor(startIndex / chunkSize)) {
      const nextChunkKey = `chunk-${currentChunk + 1}`;
      const nextData = beatsData[nextChunkKey] || [];

      beats.push(...nextData.beats);
      hesBeatStatus.push(...nextData.hesBeatStatus);
      beatChannels.push(...nextData.beatChannels);
      beatHeatMapIds.push(...nextData.beatHeatMapIds);
      beatReviewed.push(...nextData.beatReviewed);
      beatTemplateIds.push(...nextData.beatTemplateIds);
    }

    return {
      beats: beats.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx),
      hesBeatStatus: hesBeatStatus.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx),
      beatChannels: beatChannels.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx),
      beatHeatMapIds: beatHeatMapIds.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx),
      beatReviewed: beatReviewed.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx),
      beatTemplateIds: beatTemplateIds.slice(startIndex - sliceChunkIdx, endIndex - sliceChunkIdx),
    };
  };

  /**
   * Maps an absolute beat index onto the cache chunk that contains it. Divides the index by the chunk size and floors the result.
   *
   * @param startIndex - Absolute index of the beat being located.
   * @param chunkSize - Number of beats stored per chunk.
   * @returns The zero-based index of the containing chunk.
   */
  const getCurrentChunk = (startIndex: number, chunkSize: number) =>
    Math.floor(startIndex / chunkSize);

  const handleChangeBulkBeatType = ({
    type,
    strips = [] as any[],
  }: {
    type: any;
    strips?: any[];
  }) => {
    if (type === BULK_ACTION_ENUM.SWAP_TEMPLATE) {
      const targetTemplate = getBulkActionSwap();
      if (_.isEmpty(targetTemplate)) {
        return;
      }
      const {
        newBeatChanges,
        newResetBulksBeatChanges,
        newSwapBeatChanges,
        newResetBulkSwapBeats,
      } = handleSwapsMultipleBeats({
        selectedBeats: strips,
        bulkBeatsChanges: getBulkBeatsChanges(),
        beatChanges: getBeatChanges(),
        resetBulksBeatChanges: getResetBulkBeatsChanges(),
        bulkSwapBeats: getBulkSwapBeats(),
        swapBeatChanges: getSwapBeatChanges(),
        resetBulkSwapBeats: getResetSwapBeats(),
        targetTemplateId: targetTemplate?.id,
        type: activeButton,
      });
      setBeatChanges(newBeatChanges);
      setResetBulkBeatsChanges(newResetBulksBeatChanges);
      setSwapBeatChanges(newSwapBeatChanges);
      setResetSwapBeats(newResetBulkSwapBeats);
      setBeatOptions((prev: any) => ({
        ...prev,
        beatChangesState: newBeatChanges,
        resetBulkBeatsChangesState: newResetBulksBeatChanges,
        swapBeatChangesState: newSwapBeatChanges,
        resetSwapBeatsState: newResetBulkSwapBeats,
      }));
    } else if (type === BULK_ACTION_ENUM.MARK_REVIEWED) {
      // handleUpdateBeatReview doesn't declare a `strip` field (only `strips`);
      // this call site passes one anyway and it is silently unused - pre-existing.
      const { beatReviewChanges, bulkReviewResetBeats } = handleUpdateBeatReview({
        beatChanges: reviewedBeatsChanges,
        strip: selectedStrip,
        strips,
        bulkReviewedBeats,
        isReviewed: true,
        resetBulkReviewedBeats,
        activeButton,
      } as any);
      setReviewedBeatsChanges(beatReviewChanges);
      setResetBulkReviewedBeats(bulkReviewResetBeats);
      setBeatOptions((prev: any) => ({
        ...prev,
        reviewedBeatsChangesState: beatReviewChanges,
        resetBulkReviewedBeatsState: bulkReviewResetBeats,
      }));
    } else {
      const {
        newBeatChanges,
        newResetBulksBeatChanges,
        newSwapBeatChanges,
        newResetBulkSwapBeats,
      } = handleChangeTypeMultipleBeats({
        selectedBeats: strips,
        bulkBeatsChanges: getBulkBeatsChanges(),
        beatChanges: getBeatChanges(),
        resetBulksBeatChanges: getResetBulkBeatsChanges(),
        bulkSwapBeats: getBulkSwapBeats(),
        swapBeatChanges: getSwapBeatChanges(),
        resetBulkSwapBeats: getResetSwapBeats(),
        type,
        activeButton,
      });
      setBeatChanges(newBeatChanges);
      setResetBulkBeatsChanges(newResetBulksBeatChanges);
      setSwapBeatChanges(newSwapBeatChanges);
      setResetSwapBeats(newResetBulkSwapBeats);
      setBeatOptions((prev: any) => ({
        ...prev,
        beatChangesState: newBeatChanges,
        resetBulkBeatsChangesState: newResetBulksBeatChanges,
        swapBeatChangesState: newSwapBeatChanges,
        resetSwapBeatsState: newResetBulkSwapBeats,
      }));
    }
  };

  const handleCacheData = ({
    filter,
    totalBeats,
    chunkSize,
    currentChunk,
    lastResult,
    cacheKey,
  }: {
    filter: any;
    totalBeats: any;
    chunkSize: any;
    currentChunk: any;
    lastResult: any;
    cacheKey: any;
  }) => {
    const { chunks } = lastResult || {};
    const hasCurrnetChunk = chunks?.includes(currentChunk);
    const hasNextCurrentChunk = chunks?.includes(currentChunk + 1);
    const hasPrevCurrentChunk = chunks?.includes(currentChunk - 1);
    if (hasCurrnetChunk && hasNextCurrentChunk && hasPrevCurrentChunk) {
      return;
    }
    const cacheChunks: any[] = [];
    if (!hasPrevCurrentChunk && currentChunk - 1 >= 0) {
      cacheChunks.push(currentChunk - 1);
    }
    if (!hasCurrnetChunk) {
      cacheChunks.push(currentChunk);
    }
    if (!hasNextCurrentChunk) {
      cacheChunks.push(currentChunk + 1);
    }

    const _cacheKey = `_cacheKey-${cacheKey}-${currentChunk}`;
    if (!_.isEmpty(cacheChunks) && !cache.current.has(_cacheKey)) {
      cache.current.set(_cacheKey, true);
      fetchBackgroundData({
        inputFilter: filter,
        totalBeats,
        chunkSize,
        cacheChunks,
        cacheKey,
        _cacheKey,
        lastResult,
      });
    }
  };

  /**
   * Loads one page of beat strips for the selected RR cells or morphology template and pushes it into the grid. Serves the page from the chunked cache when the covering chunk is present, otherwise calls fetchHolterRrCellsBeats and records the new total, formats the beats and their timestamps, schedules background caching, re-selects a strip according to the arrow-key direction, replays any armed bulk action onto the page, and toasts on failure.
   *
   * @param rrCellIds - Heat-map cell ids to restrict the beats to, or empty for all.
   * @param selectedTemplate - Morphology template to restrict the beats to, or empty for none.
   * @param activeButton - Active beat type, deciding whether the template sort order applies.
   * @returns A promise resolving once the page has been loaded and the grid state updated.
   */
  const fetchBeats = async ({
    rrCellIds,
    selectedTemplate,
    activeButton,
  }: {
    rrCellIds: any;
    selectedTemplate: any;
    activeButton: any;
  }) => {
    const chunkSize = 10000;
    try {
      const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
      const filterKeys = {
        templateIds: !_.isEmpty(selectedTemplate) ? [selectedTemplate.id] : undefined,
        rrCellIds: _.isEmpty(rrCellIds) ? undefined : rrCellIds,
        sortBy: hasTemplateBeat(activeButton) ? getBeatsSortType() : 'Timestamp',
        reviewedBy: getVisibleReviewedFilter(),
      };
      const filter = {
        ...filterKeys,
        studyId,
        profileId,
        skip: validatePage * STRIP_BEAT_THUMBNAIL_INFO.stripDisplayLimit,
      };

      let data: any[] = [];
      let listDate: any[] = [];
      const startIndex = validatePage * STRIP_BEAT_THUMBNAIL_INFO.stripDisplayLimit;
      const currentChunk = getCurrentChunk(startIndex, chunkSize);
      const keyString = JSON.stringify(filterKeys);
      const cacheKey = `stripBeatsCache-${hashStringCode(keyString)}`;
      // const cacheKey = `stripBeatsCache-${keyString}`;
      const endIndex = (validatePage + 1) * STRIP_BEAT_THUMBNAIL_INFO.stripDisplayLimit;
      const currentCacheData: any = stripBeatsCache.has(cacheKey)
        ? stripBeatsCache.get(cacheKey)
        : {};
      const isChunkOverPage =
        Math.floor(endIndex / chunkSize) - Math.floor(startIndex / chunkSize) > 0;
      const canUseCache = !isChunkOverPage || currentCacheData?.chunks?.includes(currentChunk + 1);
      let totalCellBeats = getTotalStrip();
      if (
        stripBeatsCache.has(cacheKey) &&
        currentCacheData?.chunks?.includes(currentChunk) &&
        canUseCache
      ) {
        console.log('[stripBeats] using cache', cacheKey, currentChunk);
        const { beatsData } = stripBeatsCache.get(cacheKey) as any;
        const slicedData = getCachedDataSlice(
          beatsData,
          startIndex,
          endIndex,
          currentChunk,
          chunkSize,
        );
        data = formatBeatStripRr(
          slicedData?.beats,
          slicedData?.hesBeatStatus,
          slicedData?.beatChannels,
          slicedData?.beatHeatMapIds,
          slicedData?.beatReviewed,
          slicedData?.beatTemplateIds,
        );
        listDate = getListBeatTime(slicedData?.beats, timezoneOffset);
      } else {
        setIsLoadingStrip(true);
        console.log('[stripBeats] not using cache', cacheKey, currentChunk);
        const {
          beats,
          hesBeatStatus,
          beatChannels,
          totalBeats,
          beatHeatMapIds,
          beatReviewed,
          beatTemplateIds,
        } = await fetchHolterRrCellsBeats(
          filter,
          STRIP_BEAT_THUMBNAIL_INFO.stripDisplayLimit,
          KEY_CANCEL.API_HOLTER_AI,
        );
        data = formatBeatStripRr(
          beats,
          hesBeatStatus,
          beatChannels,
          beatHeatMapIds,
          beatReviewed,
          beatTemplateIds,
        );
        listDate = getListBeatTime(beats, timezoneOffset);

        setTotalStrip(totalBeats ?? 0);
        totalCellBeats = totalBeats;
      }

      if (totalCellBeats) {
        handleCacheData({
          filter,
          totalBeats: totalCellBeats,
          chunkSize,
          currentChunk,
          lastResult: currentCacheData,
          cacheKey,
        });
      }

      if (isActiveQuery) {
        callbackListDate(listDate);
        if (validatePage < prevPageIndex.current && arrowKeyToPrevPage.current) {
          stripPosition.current = getStripPosition(
            data.length - 1,
            STRIP_BEAT_THUMBNAIL_INFO.stripPerRow,
          );
          setSelectedStrip(data[data.length - 1]);
        } else {
          const selectedStripIndex = _.findIndex(data, (x) => x.id === selectedStrip?.id);
          const tempSelectedStrip =
            selectedStripIndex === -1 ? data[0] || null : data[selectedStripIndex];
          stripPosition.current = getStripPosition(
            selectedStripIndex === -1 ? 0 : selectedStripIndex,
            STRIP_BEAT_THUMBNAIL_INFO.stripPerRow,
          );
          setSelectedStrip(tempSelectedStrip);
        }
        const bulkAction = getBulkAction();
        if (
          bulkAction !== BULK_ACTION_ENUM.NOTHING &&
          validatePage > lastCurrentCellsHighestPage.current
        ) {
          handleChangeBulkBeatType({
            type: bulkAction,
            strips: data,
          });
          lastCurrentCellsHighestPage.current =
            validatePage > lastCurrentCellsHighestPage.current
              ? validatePage
              : lastCurrentCellsHighestPage.current;
        }
        arrowKeyToPrevPage.current = false;
        prevPageIndex.current = validatePage;
        setHolterBeats(data);
        setIsLoadingStrip(false);
      }
    } catch (error) {
      logError('Failed to fetch rr heatmap beats: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Selects the beat strip the user clicked and records where it sits in the thumbnail grid. Updates the selected strip only when the clicked beat differs from the current one, deriving the row and column from the index and the configured strips per row.
   *
   * @param beat - Beat behind the clicked thumbnail.
   * @param index - Position of that thumbnail within the page.
   * @returns Nothing.
   */
  const handleClickSelectBeat = useCallback((beat: any, index: any) => {
    setSelectedStrip((prev: any) => {
      if (beat?.id !== prev?.id) {
        stripPosition.current = getStripPosition(index, STRIP_BEAT_THUMBNAIL_INFO.stripPerRow);
        return beat;
      }
      return prev;
    });
  }, []);

  /**
   * Drops every cached page of beat strips so the next fetch goes back to the server. Clears both the in-flight request map held in a ref and the shared stripBeatsCache.
   *
   * @returns Nothing.
   */
  const restCacheStripBeats = () => {
    cache.current.clear();
    stripBeatsCache.clear();
  };

  /**
   * Disarms any bulk action pending on the RR-interval beat strips. Puts the tab-level bulk action back to NOTHING and zeroes the highest-page marker when one was armed, then always clears the per-cell bulk action.
   *
   * @returns Nothing.
   */
  const resetBulkAction = () => {
    const bulkAction = getBulkAction();
    if (bulkAction !== BULK_ACTION_ENUM.NOTHING) {
      setBulkAction(BULK_ACTION_ENUM.NOTHING);
      lastCurrentCellsHighestPage.current = 0;
    }
    setBulkActionByCell(BULK_ACTION_ENUM.NOTHING);
  };

  useEffect(() => {
    if (loadingBeatStatus.status === LOAD_BEAT_STATUS.NONE) {
      setIsLoadingStrip(false);
      setHolterBeats([]);
    }
    const activeButton = getActiveButton();

    if (loadingBeatStatus.status === LOAD_BEAT_STATUS.FETCH && isRenderViewerEcg) {
      isActiveQuery = true;
      const listSelectedCells = _.filter(getListSelectedCells(), (x) => !_.isNaN(Number(x.id)));
      const rrCellIds = _.map(listSelectedCells, (x) => x.id);
      const selectedTemplate = getSelectedTemplate();

      const currentListIds = mapFilterHeatmapIds((dataChartHeatMap as any)[activeButton]) || [];
      const diff = _.difference(rrCellIds, currentListIds);
      const queryCellIds = _.isEmpty(diff) ? rrCellIds : _.difference(rrCellIds, diff);
      if (queryCellIds.length !== 0 || !_.isEmpty(selectedTemplate)) {
        fetchBeats({ rrCellIds: queryCellIds, selectedTemplate, activeButton });
      }
    }
    if (loadingBeatStatus.status === LOAD_BEAT_STATUS.RELOAD && isRenderViewerEcg) {
      isActiveQuery = true;
      const listSelectedCells = _.filter(getListSelectedCells(), (x) => !_.isNaN(Number(x.id)));
      const rrCellIds = _.map(listSelectedCells, (x) => x.id);
      const selectedTemplate = getSelectedTemplate();
      if (rrCellIds.length !== 0 || !_.isEmpty(selectedTemplate)) {
        restCacheStripBeats();
        setLoadingBeatStatus({ status: LOAD_BEAT_STATUS.FETCH });
      } else {
        setLoadingBeatStatus({ status: LOAD_BEAT_STATUS.NONE });
      }
      const currentList = mapFilterHeatmapIds((dataChartHeatMap as any)[activeButton]) || [];
      setListSelectedCells((prev: any) => {
        const ids = _.map(prev, 'id');
        const diff = _.difference(ids, currentList);
        if (diff.length > 0) {
          const remainIds = _.difference(ids, diff);
          return _.filter(prev, (x) => remainIds.includes(x.id));
        }
        return prev;
      });
    }
    return () => {
      isActiveQuery = false;
    };
  }, [loadingBeatStatus, pageIndex, isRenderViewerEcg, visibleReviewedFilter, beatsSortType]);

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      setLoadingBeatStatus({ status: LOAD_BEAT_STATUS.RELOAD });
    }
    return () => {
      isActiveQuery = false;
    };
  }, [isRenderViewerEcg]);

  // Reset last highest page when selected cells change
  useEffect(() => {
    lastCurrentCellsHighestPage.current = 0;
    setIsRenderViewerEcg(true);
    restCacheStripBeats();
  }, [listSelectedCells, visibleReviewedFilter, beatsSortType, selectedTemplate]);

  useUpdateEffect(() => {
    if (
      !_.isEmpty(bulkActionByCell) &&
      bulkActionByCell !== BULK_ACTION_ENUM.NOTHING &&
      !isLoadingStrip
    ) {
      const listSelectedCells = _.filter(getListSelectedCells(), (x) => !_.isNaN(Number(x.id)));
      const selectedTemplate = getSelectedTemplate();
      if (_.isEmpty(listSelectedCells) && _.isEmpty(selectedTemplate)) {
        resetBulkAction();
        return;
      }
      if (bulkActionByCell === BULK_ACTION_ENUM.MARK_REVIEWED) {
        // isReviewed = true
        const currentListCells = _.cloneDeep(listSelectedCells);
        const cloneBulkReview = _.cloneDeep(getBulkReviewedBeats());
        const cloneResetList = _.cloneDeep(getResetBulkReviewedBeats());
        const cloneReviewBeatChanges = _.cloneDeep(getReviewedBeatsChanges());
        const input = {
          selectedTemplate,
          currentListCells,
          cloneBulkReview,
          cloneResetList,
          cloneReviewBeatChanges,
          isReviewed: true,
          activeButton,
        };
        if (_.isEmpty(currentListCells)) {
          if (!_.isEmpty(selectedTemplate)) {
            handleMarkReviewByTemplateOnly(input);
          }
        } else if (_.isEmpty(selectedTemplate)) {
          handleMarkReviewByCellOnly(input);
        } else {
          handleMarkReviewByTemplateAndCell(input);
        }
        setBulkReviewedBeats(cloneBulkReview);
        setResetBulkReviewedBeats(cloneResetList);
        setReviewedBeatsChanges(cloneReviewBeatChanges);
        setBeatOptions((prev: any) => ({
          ...prev,
          reviewedBeatsChangesState: cloneReviewBeatChanges,
          bulkReviewedBeatsState: cloneBulkReview,
          resetBulkReviewedBeatsState: cloneResetList,
        }));
      } else {
        const input = {
          activeButton: getActiveButton(),
          bulkAction: bulkActionByCell,
          bulkBeatsChange: getBulkBeatsChanges(),
          beatChanges: getBeatChanges(),
          resetBulkBeatsChanges: getResetBulkBeatsChanges(),
          resetBulkSwapChanges: getResetSwapBeats(),
          bulkSwapChanges: getSwapBeatChanges(),
          swapBeatChanges: getSwapBeatChanges(),
          listSelectedCells,
          selectedTemplate,
        };
        const {
          cloneBeatChanges,
          cloneBulkBeatChange,
          cloneResetBeats,
          cloneSwapBeatChanges,
          cloneBulkSwapChanges,
          cloneResetSwapList,
        } = handleUpdateBulkAction(input);

        setBeatChanges(cloneBeatChanges);
        setBulkBeatsChanges(cloneBulkBeatChange);
        setResetBulkBeatsChanges(cloneResetBeats);

        setSwapBeatChanges(cloneSwapBeatChanges);
        setBulkSwapBeats(cloneBulkSwapChanges);
        setResetSwapBeats(cloneResetSwapList);
        setBeatOptions((prev: any) => ({
          ...prev,
          beatChangesState: cloneBeatChanges,
          bulkBeatsChangesState: cloneBulkBeatChange,
          resetBulkBeatsChangesState: cloneResetBeats,
          swapBeatChangesState: cloneSwapBeatChanges,
          bulkSwapBeatsState: cloneBulkSwapChanges,
          resetSwapBeatsState: cloneResetSwapList,
        }));
      }
      resetBulkAction();
    }
  }, [bulkActionByCell]);

  useEffect(() => {
    /**
     * Moves the beat-strip selection around the thumbnail grid with the arrow keys. Ignores auto-repeat, presses made while the page is loading and presses made while the pointer sits outside the beats tab, resets the drag selection, then steps the cached row or column position and selects the beat at the new index, emitting the next- or previous-page event instead when the move runs off the loaded page.
     *
     * @param event - Keydown event carrying the pressed key.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(stripPosition.current, STRIP_BEAT_THUMBNAIL_INFO.stripPerRow);
          if (index > holterBeats.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.BEAT_HR_NEXT);
          } else {
            const beat = holterBeats[index];
            if (beat) {
              setSelectedStrip(beat);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(stripPosition.current, STRIP_BEAT_THUMBNAIL_INFO.stripPerRow);
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.BEAT_HR_PREV);
          } else {
            const beat = holterBeats[index];
            if (beat) {
              setSelectedStrip(beat);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const beat =
            holterBeats[
              getStripIndex(stripPosition.current, STRIP_BEAT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (beat) {
            setSelectedStrip(beat);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const beat =
            holterBeats[
              getStripIndex(stripPosition.current, STRIP_BEAT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (beat) {
            setSelectedStrip(beat);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [holterBeats]);

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton as any, timeoutRef.current);
    },
    [activeButton],
  );

  useUpdateEffect(() => {
    if (loadingBeatStatus.status === LOAD_BEAT_STATUS.FETCH) {
      const { x: row, y: column } = stripPosition.current;
      const currentStripIndexInList = column + row * STRIP_BEAT_THUMBNAIL_INFO.prevStripPerRow;
      const currentBeatIndex =
        currentStripIndexInList + pageIndex.index * STRIP_BEAT_THUMBNAIL_INFO.prevStripDisplayLimit;
      const newPage = Math.floor(currentBeatIndex / STRIP_BEAT_THUMBNAIL_INFO.stripDisplayLimit);
      setPageIndex({ index: newPage < 0 ? 0 : newPage });
    }
  }, [sizeThumbnailConfig]);

  useUpdateEffect(() => {
    if (bulkAction !== BULK_ACTION_ENUM.NOTHING && !isLoadingStrip) {
      handleChangeBulkBeatType({
        type: bulkAction,
        strips: holterBeats,
      });
      const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
      lastCurrentCellsHighestPage.current =
        validatePage > lastCurrentCellsHighestPage.current
          ? validatePage
          : lastCurrentCellsHighestPage.current;
    }
  }, [bulkAction, bulkActionSwap]);

  return {
    loadingBeatStatus,
    activeButton,
    listSelectedCells,
    selectedTemplate,
    holterBeats,
    isLoadingStrip,
    selectionAreaWrapperRef,
    setSelectedBeat,
    selectedStrip,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
    highPassThumbnail,
    handleClickSelectBeat,
  };
};

export default useStripBeats;
